import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, Stack, Switch,
  TextField, Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import RuleIcon from '@mui/icons-material/Rule';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { regOps } from '@/api/endpoints';
import { formatDate } from '@/utils/format';
import type { MedicalJurisdiction } from '@/types/medical';

/**
 * RegulationOps — rules.
 *
 * A rule is a typed JSON predicate that says "for jurisdiction X and rule
 * type Y, given input I, the regulator effect is `block` / `warn` / `note`
 * with reason Z". The body is freeform JSON because the rule engine uses
 * a small DSL-of-JSON underneath; the editor doesn't validate it
 * (regulation_engine does), but it does validate JSON syntactically.
 */

type RegRule = {
  id: number;
  jurisdiction: number;
  rule_type: string;
  name: string;
  description: string;
  body: Record<string, unknown>;
  effect: 'block' | 'warn' | 'note';
  valid_from: string;
  valid_to: string | null;
  legal_reference: string;
  sources: number[];
  is_active: boolean;
};

const RULE_TYPES = [
  { code: 'prescribing_dispense_allowed', label: 'Prescribing — dispense allowed' },
  { code: 'prescribing_controlled',       label: 'Prescribing — controlled substance' },
  { code: 'prescribing_quantity_limit',   label: 'Prescribing — quantity limit' },
  { code: 'telemedicine_allowed',         label: 'Telemedicine allowed' },
  { code: 'cross_border_telemedicine',    label: 'Cross-border telemedicine' },
  { code: 'sick_note_max_duration',       label: 'Sick note — max duration' },
  { code: 'minor_consent_required',       label: 'Consent — minor' },
  { code: 'data_residency',               label: 'Data residency' },
];

const EFFECTS = [
  { code: 'block', label: 'Block', color: 'error' as const },
  { code: 'warn',  label: 'Warn',  color: 'warning' as const },
  { code: 'note',  label: 'Note',  color: 'default' as const },
];

export function RegOpsRulesPage() {
  const [params] = useSearchParams();
  const filterJurisdictionId = params.get('jurisdiction');

  const rulesQ = useApiQuery({
    queryKey: ['regops', 'rules'],
    queryFn:  regOps.rules.list as () => Promise<RegRule[]>,
  });
  const jurisdictionsQ = useApiQuery({
    queryKey: ['regops', 'jurisdictions'],
    queryFn:  regOps.jurisdictions.list as () => Promise<MedicalJurisdiction[]>,
  });

  const [editing, setEditing] = useState<RegRule | null>(null);
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const all = rulesQ.data ?? [];
    if (!filterJurisdictionId) return all;
    return all.filter((r) => r.jurisdiction === parseInt(filterJurisdictionId, 10));
  }, [rulesQ.data, filterJurisdictionId]);

  const jurisdictionMap = useMemo(() => {
    const m = new Map<number, MedicalJurisdiction>();
    (jurisdictionsQ.data ?? []).forEach((j) => m.set(j.id, j));
    return m;
  }, [jurisdictionsQ.data]);

  return (
    <>
      <PageHeader
        title="Regulation rules"
        subtitle="Each rule is a typed JSON predicate evaluated by the regulation engine."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New rule
          </Button>
        }
      />

      <DataState query={rulesQ} empty={{ title: 'No rules yet' }}>
        {() => filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No rules for this filter.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {filtered.map((r) => (
              <Row key={r.id} rule={r}
                   jurisdictionCode={jurisdictionMap.get(r.jurisdiction)?.code ?? '?'}
                   onEdit={() => setEditing(r)} />
            ))}
          </Stack>
        )}
      </DataState>

      {(editing || creating) && (
        <RuleDialog
          rule={editing}
          jurisdictions={jurisdictionsQ.data ?? []}
          onClose={() => { setEditing(null); setCreating(false); }}
        />
      )}
    </>
  );
}

function Row({
  rule, jurisdictionCode, onEdit,
}: { rule: RegRule; jurisdictionCode: string; onEdit: () => void }) {
  const effect = EFFECTS.find((e) => e.code === rule.effect);
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <RuleIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {rule.name}
              </Typography>
              <Chip size="small" label={jurisdictionCode}
                    variant="outlined" className="tabular" />
              <Chip size="small" label={rule.rule_type.replace(/_/g, ' ')}
                    variant="outlined" sx={{ textTransform: 'capitalize' }} />
              {effect && (
                <Chip size="small" label={effect.label} color={effect.color}
                      variant="outlined" />
              )}
              {!rule.is_active && (
                <Chip size="small" label="inactive" variant="outlined" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" className="tabular">
              v{formatDate(rule.valid_from)}
              {rule.valid_to && ` – ${formatDate(rule.valid_to)}`}
              {rule.sources?.length > 0 && ` · ${rule.sources.length} source(s)`}
            </Typography>
            {rule.description && (
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }} noWrap>
                {rule.description}
              </Typography>
            )}
          </Box>
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>Edit</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function RuleDialog({
  rule, jurisdictions, onClose,
}: { rule: RegRule | null; jurisdictions: MedicalJurisdiction[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = rule !== null;

  const [jurisdictionId, setJurisdictionId] = useState(rule?.jurisdiction ? String(rule.jurisdiction) : '');
  const [ruleType, setRuleType] = useState(rule?.rule_type ?? 'prescribing_dispense_allowed');
  const [name, setName] = useState(rule?.name ?? '');
  const [description, setDescription] = useState(rule?.description ?? '');
  const [body, setBody] = useState(JSON.stringify(rule?.body ?? {}, null, 2));
  const [effect, setEffect] = useState(rule?.effect ?? 'block');
  const [validFrom, setValidFrom] = useState(rule?.valid_from ?? '');
  const [validTo, setValidTo] = useState(rule?.valid_to ?? '');
  const [legalRef, setLegalRef] = useState(rule?.legal_reference ?? '');
  const [isActive, setIsActive] = useState(rule?.is_active ?? true);

  useEffect(() => {
    setJurisdictionId(rule?.jurisdiction ? String(rule.jurisdiction) : '');
    setRuleType(rule?.rule_type ?? 'prescribing_dispense_allowed');
    setName(rule?.name ?? '');
    setDescription(rule?.description ?? '');
    setBody(JSON.stringify(rule?.body ?? {}, null, 2));
    setEffect(rule?.effect ?? 'block');
    setValidFrom(rule?.valid_from ?? '');
    setValidTo(rule?.valid_to ?? '');
    setLegalRef(rule?.legal_reference ?? '');
    setIsActive(rule?.is_active ?? true);
  }, [rule?.id]);

  let bodyValid = true;
  let parsedBody: Record<string, unknown> = {};
  try { parsedBody = JSON.parse(body); } catch { bodyValid = false; }

  const saveMut = useApiMutation({
    mutationFn: () => {
      const payload = {
        jurisdiction: parseInt(jurisdictionId, 10),
        rule_type: ruleType,
        name, description,
        body: parsedBody,
        effect,
        valid_from: validFrom,
        valid_to: validTo || null,
        legal_reference: legalRef,
        is_active: isActive,
      };
      return isEdit
        ? regOps.rules.update(rule!.id, payload)
        : regOps.rules.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Rule saved.' : 'Rule created.');
      qc.invalidateQueries({ queryKey: ['regops', 'rules'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = jurisdictionId && name.trim() && validFrom && bodyValid;

  return (
    <Dialog open onClose={() => !saveMut.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit rule' : 'New rule'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select required label="Jurisdiction"
                       value={jurisdictionId}
                       onChange={(e) => setJurisdictionId(e.target.value)}>
              {jurisdictions.map((j) => (
                <MenuItem key={j.id} value={String(j.id)}>{j.code}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Rule type"
                       value={ruleType}
                       onChange={(e) => setRuleType(e.target.value)}>
              {RULE_TYPES.map((t) => (
                <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required label="Name"
                       value={name} onChange={(e) => setName(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Description"
                       value={description}
                       onChange={(e) => setDescription(e.target.value)}
                       helperText="What does this rule express in plain language?" />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth select label="Effect"
                       value={effect} onChange={(e) => setEffect(e.target.value as RegRule['effect'])}>
              {EFFECTS.map((e) => (
                <MenuItem key={e.code} value={e.code}>{e.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth required type="date" label="Valid from"
                       value={validFrom}
                       onChange={(e) => setValidFrom(e.target.value)}
                       InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField fullWidth type="date" label="Valid to"
                       value={validTo}
                       onChange={(e) => setValidTo(e.target.value)}
                       InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Legal reference"
                       value={legalRef} onChange={(e) => setLegalRef(e.target.value)}
                       placeholder="e.g. Ustawa o zawodach lekarza i lekarza dentysty Art. 42" />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={8} label="Body (JSON predicate)"
              error={!bodyValid}
              helperText={bodyValid
                ? 'JSON document the regulation engine evaluates. See `regulation_engine.md`.'
                : 'Invalid JSON.'}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              sx={{ '& textarea': { fontFamily: 'ui-monospace, monospace', fontSize: '0.875rem' } }}
            />
            {!bodyValid && (
              <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                Body must be valid JSON before saving.
              </Alert>
            )}
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={isActive}
                               onChange={(_, v) => setIsActive(v)} />}
              label="Active" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || saveMut.isPending}
                onClick={() => saveMut.mutate()}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
